import { useCallback, useEffect, useState } from "react";
import { useEstablishments } from "@/context/EstablishmentContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, Loader2 } from "lucide-react";
import { GoogleBusinessCard } from "@/components/GoogleBusinessCard";
import { GoogleOverview } from "@/components/google/GoogleOverview";
import { GoogleProfileDiag } from "@/components/google/GoogleProfileDiag";
import { GoogleReviews } from "@/components/google/GoogleReviews";
import { GooglePerformance } from "@/components/google/GooglePerformance";
import { GoogleReplySettings } from "@/components/google/GoogleReplySettings";
import { GooglePosts } from "@/components/google/GooglePosts";
import { GoogleReviewKit } from "@/components/google/GoogleReviewKit";

export default function GoogleBusiness() {
  const { current } = useEstablishments();
  const [dash, setDash] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [filter, setFilter] = useState(() => new URLSearchParams(window.location.search).get("filter") || "");
  const load = useCallback(async () => {
    if (!current) return;
    try { const { data } = await api.get(`/google/dashboard/${current.id}`); setDash(data); } catch (e) { setDash({ error: formatApiErrorDetail(e.response?.data?.detail) || "Erreur" }); }
  }, [current]);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post(`/google/sync/${current.id}`); toast[data.ok ? "success" : "warning"](data.ok ? `Synchronisé : ${data.reviews} avis mis à jour` : `Synchronisation partielle : ${(data.errors_user || []).map((e) => e.label).join(", ")} en erreur (détails ci-dessous)`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); } finally { setSyncing(false); }
  };
  if (!current) return null;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="google-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Google Business Profile</h1>
          <p className="text-slate-500 text-sm mt-1">Fiche, avis, réponses et performances Google de {current.name} — uniquement des données réellement fournies par Google.</p>
        </div>
        {dash?.has_location && <Button variant="outline" onClick={sync} disabled={syncing} className="rounded-xl" data-testid="gbp-sync">{syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Resynchroniser</Button>}
      </div>
      {dash?.last_sync_at && <p className="text-xs text-slate-400 mt-1" data-testid="gbp-last-sync">Dernière synchronisation : {new Date(dash.last_sync_at).toLocaleString("fr-FR")}</p>}
      {dash?.sync_errors?.length > 0 && <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm space-y-1.5" data-testid="gbp-sync-errors">
        <div className="font-semibold text-amber-800">Synchronisation partielle — {dash.sync_errors.length} bloc(s) en erreur</div>
        {dash.sync_errors.map((e, i) => <div key={i} className="flex gap-2"><span className="shrink-0 px-2 py-0.5 rounded-full bg-white border border-amber-200 text-xs text-amber-800">{e.label}</span><span className="text-slate-700">{e.message}</span></div>)}
        {dash.sync_errors.some((e) => e.kind === "reauth") && <Button size="sm" variant="outline" className="rounded-lg mt-1" onClick={() => document.querySelector("[data-testid=gbp-connect]")?.click()} data-testid="gbp-reauth">Reconnecter Google</Button>}
      </div>}
      <div className="mt-6"><GoogleBusinessCard estId={current.id} onChange={load} /></div>
      {dash?.has_location && (
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setFilter(""); }} className="mt-6">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 rounded-xl p-1">
            <TabsTrigger value="overview" data-testid="gbp-tab-overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="profile" data-testid="gbp-tab-profile">Ma fiche</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="gbp-tab-reviews">Avis</TabsTrigger>
            <TabsTrigger value="performance" data-testid="gbp-tab-performance">Performances</TabsTrigger>
            <TabsTrigger value="posts" data-testid="gbp-tab-posts">Publications</TabsTrigger>
            <TabsTrigger value="kit" data-testid="gbp-tab-kit">Demander un avis</TabsTrigger>
            <TabsTrigger value="settings" data-testid="gbp-tab-settings">Réponses aux avis</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><GoogleOverview dash={dash} onNavigate={(t, f) => { setTab(t); setFilter(f || ""); }} /></TabsContent>
          <TabsContent value="profile"><GoogleProfileDiag estId={current.id} /></TabsContent>
          <TabsContent value="reviews"><GoogleReviews estId={current.id} onChange={load} initialFilter={filter} /></TabsContent>
          <TabsContent value="performance"><GooglePerformance estId={current.id} /></TabsContent>
          <TabsContent value="posts"><GooglePosts estId={current.id} /></TabsContent>
          <TabsContent value="kit"><GoogleReviewKit estId={current.id} /></TabsContent>
          <TabsContent value="settings"><GoogleReplySettings estId={current.id} onSaved={load} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
